import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { ReactNode } from 'react';

import { GeoArea } from '@/types/GeoArea';
import { slugToTitleCase } from '@/utils/strings';

import { AuctionsNearMeRouteParams } from '../types/AuctionsNearMe.types';

type GetPageTitleParams = {
    geoArea: GeoArea | undefined;
    geocoderFailed: boolean;
    hasFilteredResults: boolean;
    routeParams: AuctionsNearMeRouteParams;
    searchTerm: string;
};

// Derive location name from URL params for SSR when geoArea is not found
function getLocationFromRouteParams(routeParams: AuctionsNearMeRouteParams): string | undefined {
    if ('city' in routeParams && routeParams.city) {
        const decodedCity = decodeURIComponent(routeParams.city);
        const formattedCity = slugToTitleCase(decodedCity);

        if ('state' in routeParams && routeParams.state) {
            const formattedState = decodeURIComponent(routeParams.state).toUpperCase();
            return `${formattedCity}, ${formattedState}`;
        }
        return formattedCity;
    }

    if ('state' in routeParams && routeParams.state) {
        return slugToTitleCase(decodeURIComponent(routeParams.state));
    }

    if ('country' in routeParams && routeParams.country) {
        return slugToTitleCase(decodeURIComponent(routeParams.country));
    }

    return undefined;
}

export function getPageTitle({
    geoArea,
    geocoderFailed,
    hasFilteredResults,
    routeParams,
    searchTerm,
}: GetPageTitleParams): ReactNode {
    // User searches for a location that does not match any geo area (geocoder failed) - e.g. "Anytown"
    if (geocoderFailed && searchTerm) {
        return (
            <FormattedMessage
                id="auctionsNearMe.noAuctionsFound"
                values={{ locationSearched: searchTerm }}
            />
        );
    }

    // User searches for a location via geocoder and returns NO results - e.g. "Akron"
    if (searchTerm && !hasFilteredResults) {
        return (
            <FormattedMessage
                id="auctionsNearMe.noAuctionsFound"
                values={{ locationSearched: searchTerm }}
            />
        );
    }

    // User searches for a location via geocoder and HAS results - e.g. "Cleveland"
    if (searchTerm && hasFilteredResults) {
        return (
            <FormattedMessage
                id="auctionsNearMe.customSearchPageTitle"
                values={{ locationSearched: searchTerm }}
            />
        );
    }

    // User navigates to a geo area page
    if (geoArea) {
        // No results for this geo area
        if (!hasFilteredResults) {
            return (
                <FormattedMessage
                    id="auctionsNearMe.noAuctionsFound"
                    values={{ locationSearched: geoArea.geoAreaName }}
                />
            );
        }

        // Has results - use pageTitle if available, otherwise use geoAreaName
        if (geoArea.pageTitle) {
            return geoArea.pageTitle;
        }

        // Fallback to geoAreaName-based title for SSR when pageTitle might not be set
        return (
            <FormattedMessage
                id="auctionsNearMe.customSearchPageTitle"
                values={{ locationSearched: geoArea.geoAreaName }}
            />
        );
    }

    // SSR fallback: derive location from URL params when no geoArea is found
    // This handles URLs like /pa/pineville where pineville isn't a predefined geoArea
    const locationFromUrl = getLocationFromRouteParams(routeParams);
    if (locationFromUrl) {
        if (!hasFilteredResults) {
            return (
                <FormattedMessage
                    id="auctionsNearMe.noAuctionsFound"
                    values={{ locationSearched: locationFromUrl }}
                />
            );
        }
        return (
            <FormattedMessage
                id="auctionsNearMe.customSearchPageTitle"
                values={{ locationSearched: locationFromUrl }}
            />
        );
    }

    // Default page title (base page with no geoArea)
    return <FormattedMessage id="auctionsNearMe.defaultPageTitle" />;
}
